/**
 * Auth helper functions with timeout protection
 */

import { SupabaseClient } from '@supabase/supabase-js';

/**
 * Wraps supabase auth calls with a timeout to prevent hanging
 * @param promise The auth promise to execute with timeout
 * @param timeoutMs Timeout in milliseconds
 * @returns Result of the promise or null if timeout occurs
 */
export async function withTimeout<T>(promise: Promise<T>, timeoutMs = 5000): Promise<T | null> {
  let timeoutId: NodeJS.Timeout;
  
  const timeoutPromise = new Promise<null>((resolve) => {
    timeoutId = setTimeout(() => {
      console.warn(`Auth operation timed out after ${timeoutMs}ms`);
      resolve(null);
    }, timeoutMs);
  });

  try {
    const result = await Promise.race([promise, timeoutPromise]);
    clearTimeout(timeoutId!);
    return result;
  } catch (error) {
    clearTimeout(timeoutId!);
    console.error('Auth operation failed:', error);
    throw error;
  }
}

/**
 * Gets the current user with timeout protection
 * @param supabase Supabase client
 * @param timeoutMs Timeout in milliseconds
 * @returns The user or null if timeout occurs or no user is found
 */
export async function getCurrentUser(supabase: SupabaseClient, timeoutMs = 5000) {
  try {
    const result = await withTimeout(supabase.auth.getUser(), timeoutMs);
    return result?.data?.user || null;
  } catch (error) {
    console.error('Error getting current user:', error);
    return null;
  }
}

/**
 * Gets the current session with timeout protection
 * @param supabase Supabase client
 * @param timeoutMs Timeout in milliseconds
 * @returns The session or null if timeout occurs or no session is found
 */
export async function getCurrentSession(supabase: SupabaseClient, timeoutMs = 5000) {
  try {
    const result = await withTimeout(supabase.auth.getSession(), timeoutMs);
    return result?.data?.session || null;
  } catch (error) {
    console.error('Error getting current session:', error);
    return null;
  }
}